import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import {
  dropItemSetApi,
  equipmentCategoryApi,
  DropItemSet,
  StatInfo,
} from "../../services/dropItemApi";
import "./ResourceTables.css";
const { Title, Text } = Typography;
const tiers = [2, 3, 4, 5] as const;
const DropItemSetTab: React.FC = () => {
  const [sort, setSort] = useState("name");
  const [sets, setSets] = useState<DropItemSet[]>([]),
    [stats, setStats] = useState<StatInfo[]>([]);
  const [loading, setLoading] = useState(false),
    [saving, setSaving] = useState(false),
    [error, setError] = useState(""),
    [search, setSearch] = useState("");
  const [editor, setEditor] = useState<{ record?: DropItemSet } | null>(null);
  const [form] = Form.useForm();
  const [notice, holder] = message.useMessage();
  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [rows, ref] = await Promise.all([
        dropItemSetApi.getAll(),
        equipmentCategoryApi.getStats(),
      ]);
      setSets(rows);
      setStats(ref || []);
    } catch {
      setError("아이템 세트를 불러오지 못했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);
  const open = (record?: DropItemSet) => {
    form.resetFields();
    form.setFieldsValue(record || {});
    setEditor({ record });
  };
  const save = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (editor?.record) {
        const patch: Partial<DropItemSet> = {};
        [
          "set_name",
          ...tiers.flatMap((n) => [`set${n}_stat_key`, `set${n}_value`]),
        ].forEach((key) => {
          const next = values[key] ?? null;
          if (next !== ((editor.record as any)[key] ?? null))
            (patch as any)[key] = next;
        });
        if (Object.keys(patch).length)
          await dropItemSetApi.update(editor.record.set_key, patch);
      } else {
        await dropItemSetApi.create(
          Object.fromEntries(
            Object.entries(values).map(([k, v]) => [k, v ?? null]),
          ) as unknown as DropItemSet,
        );
      }
      setEditor(null);
      notice.success("아이템 세트를 저장했습니다.");
      await load();
    } catch (e: any) {
      if (!e.errorFields)
        notice.error("저장하지 못했습니다. 입력 내용은 유지됩니다.");
    } finally {
      setSaving(false);
    }
  };
  const bonus = (record: DropItemSet, n: number) => {
    const key = (record as any)[`set${n}_stat_key`],
      value = (record as any)[`set${n}_value`];
    if (!key && value == null) return <Text type="secondary">미설정</Text>;
    return (
      <span>
        {key ? stats.find((s) => s.key === key)?.name || key : "능력치 미지정"}{" "}
        · {value == null ? "값 미입력" : value}
      </span>
    );
  };
  return (
    <div className="resource-page">
      {holder}
      <div className="resource-toolbar">
        <div>
          <Title level={4}>아이템 세트</Title>
          <Text type="secondary">
            장착 개수별 보너스를 확인하고, 편집창에서 수정하세요.
          </Text>
        </div>
        <Space>
          <Button onClick={load} loading={loading}>
            새로고침
          </Button>
          <Button type="primary" onClick={() => open()}>
            세트 추가
          </Button>
        </Space>
      </div>
      {error && (
        <Alert type="error" message={error} style={{ marginBottom: 16 }} />
      )}
      <Space className="resource-filters" wrap>
        <Input.Search
          aria-label="아이템 세트 검색"
          placeholder="세트 이름 또는 코드 검색"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ width: 300 }}
        />
        <Select
          aria-label="세트 정렬"
          value={sort}
          onChange={setSort}
          style={{ width: 150 }}
          options={[
            { value: "name", label: "이름 오름차순" },
            { value: "name-desc", label: "이름 내림차순" },
            { value: "key", label: "코드 오름차순" },
            { value: "key-desc", label: "코드 내림차순" },
          ]}
        />
        <Text type="secondary">전체 {sets.length}개</Text>
      </Space>
      <Table
        className="resource-table"
        rowKey="set_key"
        loading={loading}
        scroll={{ x: 850 }}
        dataSource={sets
          .filter((s) =>
            `${s.set_name} ${s.set_key}`
              .toLowerCase()
              .includes(search.toLowerCase()),
          )
          .sort((a, b) => {
            const field = sort.startsWith("key") ? "set_key" : "set_name";
            return (
              (a[field] || "").localeCompare(b[field] || "") *
              (sort.endsWith("-desc") ? -1 : 1)
            );
          })}
        pagination={{ defaultPageSize: 10, showSizeChanger: true }}
        columns={[
          {
            title: "세트 이름",
            dataIndex: "set_name",
            render: (name) => <Text strong>{name}</Text>,
          },
          ...tiers.map((n) => ({
            title: `${n}개 장착`,
            key: String(n),
            render: (_: any, r: DropItemSet) => bonus(r, n),
          })),
          {
            title: "관리",
            key: "actions",
            render: (_: any, r: DropItemSet) => (
              <Space>
                <Button onClick={() => open(r)}>편집</Button>
                <Popconfirm
                  title="아이템 세트를 삭제할까요?"
                  onConfirm={async () => {
                    try {
                      await dropItemSetApi.delete(r.set_key);
                      await load();
                    } catch {
                      notice.error(
                        "삭제하지 못했습니다. 연결된 아이템을 확인해주세요.",
                      );
                    }
                  }}
                  okText="삭제"
                  cancelText="취소"
                >
                  <Button danger>삭제</Button>
                </Popconfirm>
              </Space>
            ),
          },
        ]}
        expandable={{
          expandedRowRender: (r) => (
            <div className="resource-details">
              <Text type="secondary">세트 코드 </Text>
              <Text code copyable>
                {r.set_key}
              </Text>
              <p>
                생성일: {r.created_at || "미입력"} · 수정일:{" "}
                {r.updated_at || "미입력"}
              </p>
            </div>
          ),
        }}
      />
      <Modal
        open={!!editor}
        forceRender
        title={editor?.record ? "아이템 세트 편집" : "아이템 세트 추가"}
        onCancel={() => {
          if (!saving) setEditor(null);
        }}
        onOk={save}
        confirmLoading={saving}
        okText="저장"
        cancelText="취소"
        width={640}
        closable={!saving}
        maskClosable={!saving}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          <Form.Item
            name="set_name"
            label="세트 이름"
            rules={[{ required: true, message: "세트 이름을 입력해주세요." }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="set_key"
            label="세트 코드"
            rules={[{ required: true, message: "세트 코드를 입력해주세요." }]}
          >
            <Input disabled={!!editor?.record} placeholder="SET_XXX" />
          </Form.Item>
          <Text type="secondary">
            각 장착 개수의 능력치와 값을 설정합니다. 사용하지 않는 보너스는
            비워두세요.
          </Text>
          {tiers.map((n) => (
            <div key={n} className="set-bonus-row">
              <Text strong>{n}개 장착</Text>
              <Form.Item name={`set${n}_stat_key`} label={`${n}개 장착 능력치`}>
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="능력치 선택"
                  options={stats.map((s) => ({
                    value: s.key,
                    label: s.name || s.key,
                  }))}
                />
              </Form.Item>
              <Form.Item name={`set${n}_value`} label={`${n}개 장착 값`}>
                <InputNumber style={{ width: "100%" }} />
              </Form.Item>
            </div>
          ))}
        </Form>
      </Modal>
    </div>
  );
};
export default DropItemSetTab;
